import { Capacitor } from '@capacitor/core';
import { Haptics, ImpactStyle } from '@capacitor/haptics';
import {
  applyNativeStatusBar,
  type NativeStatusBarOptions,
} from '../native/statusBar';

export interface CapacitorPlatformHook {
  isNative: boolean;
  platform: string;
  isIOS: boolean;
  isAndroid: boolean;
  applyStatusBar: (options: NativeStatusBarOptions) => Promise<void>;
  /** @deprecated Prefer applyStatusBar — style-only, no background color. */
  setStatusBarStyle: (isDark: boolean) => Promise<void>;
  /** @deprecated Prefer applyStatusBar — Android background color only. */
  setStatusBarColor: (color: string) => Promise<void>;
  triggerHaptic: (style?: ImpactStyle) => Promise<void>;
}

export const useCapacitorPlatform = (): CapacitorPlatformHook => {
  const isNative = Capacitor.isNativePlatform();
  const platform = Capacitor.getPlatform();
  const isIOS = platform === 'ios';
  const isAndroid = platform === 'android';

  const setStatusBarStyle = async (isDark: boolean) => {
    await applyNativeStatusBar({ iconStyle: isDark ? 'dark' : 'light' });
  };

  const setStatusBarColor = async (color: string) => {
    await applyNativeStatusBar({ backgroundColor: color });
  };

  const triggerHaptic = async (style: ImpactStyle = ImpactStyle.Light) => {
    if (!isNative || !Capacitor.isPluginAvailable('Haptics')) return;

    try {
      await Haptics.impact({ style });
    } catch {
      // Haptics unavailable — ignore silently.
    }
  };

  return {
    isNative,
    platform,
    isIOS,
    isAndroid,
    applyStatusBar: applyNativeStatusBar,
    setStatusBarStyle,
    setStatusBarColor,
    triggerHaptic,
  };
};
